import { useEffect, useState } from "react";
import { Wallet, Loader2, Check, X, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fcfa } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

export function AdminCaisse() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/admin/caisse").then(({ data }) => setData(data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const act = async (depId, action) => {
    setBusy(depId);
    try {
      await api.post(`/admin/caisse/versements/${depId}/${action}`);
      toast.success(action === "validate" ? "Versement validé ✅" : "Versement rejeté");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (data === null) return <div className="bg-white border border-brand-line rounded-2xl p-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;
  if (data === false) return null;

  return (
    <div data-testid="admin-caisse" className="bg-white border border-brand-line rounded-2xl p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-9 h-9 rounded-xl bg-brand/15 text-brand flex items-center justify-center"><Wallet className="w-5 h-5" /></span>
        <div>
          <h2 className="font-heading font-bold text-brand-ink">Caisse vendeurs</h2>
          <p className="text-xs text-brand-muted">Alerte si solde à remettre &gt; {fcfa(data.threshold)}.</p>
        </div>
      </div>

      {/* Rapprochement trésorerie global */}
      <div className={`grid grid-cols-3 gap-2 mb-4 rounded-xl p-3 ${data.over_threshold_global ? "bg-red-50 border border-red-200" : "bg-brand-cream"}`} data-testid="tresorerie-summary">
        <div>
          <p className="text-[11px] text-brand-muted">Total encaissé</p>
          <p className="font-heading font-black text-brand-ink">{fcfa(data.total_collected)}</p>
        </div>
        <div>
          <p className="text-[11px] text-brand-muted">Déposé (validé)</p>
          <p className="font-heading font-black text-green-700">{fcfa(data.total_deposited)}</p>
        </div>
        <div>
          <p className="text-[11px] text-brand-muted flex items-center gap-1">À remettre {data.over_threshold_global && <AlertTriangle className="w-3 h-3 text-red-600" />}</p>
          <p className={`font-heading font-black ${data.over_threshold_global ? "text-red-700" : "text-amber-700"}`}>{fcfa(data.total_balance)}</p>
        </div>
      </div>
      {data.over_threshold_global && (
        <p className="text-xs text-red-600 -mt-2 mb-3" data-testid="tresorerie-alert">⚠ Le total à remettre dépasse votre seuil ({fcfa(data.threshold)}). Demandez un dépôt aux vendeurs et croisez avec votre tableau PayGate.</p>
      )}

      {data.vendeurs.length === 0 ? <p className="text-sm text-brand-muted">Aucun vendeur.</p> : (
        <div className="space-y-2">
          {data.vendeurs.map((v) => (
            <div key={v.seller_id} data-testid={`caisse-row-${v.seller_id}`} className={`flex flex-wrap items-center justify-between gap-3 border rounded-xl px-4 py-3 ${v.over_threshold ? "border-red-300 bg-red-50" : "border-brand-line"}`}>
              <div className="flex items-center gap-2">
                {v.over_threshold && <AlertTriangle className="w-4 h-4 text-red-600" />}
                <div>
                  <p className="font-semibold text-brand-ink text-sm">{v.name}</p>
                  <p className="text-xs text-brand-muted">Encaissé {fcfa(v.collected)} · Versé {fcfa(v.deposited)}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[11px] text-brand-muted">Solde à remettre</p>
                <p className={`font-heading font-black ${v.over_threshold ? "text-red-700" : "text-amber-700"}`}>{fcfa(v.balance)}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Versements à valider */}
      <p className="text-xs font-semibold text-brand-muted mt-5 mb-2">VERSEMENTS À VALIDER ({data.pending_deposits.length})</p>
      {data.pending_deposits.length === 0 ? <p className="text-sm text-brand-muted">Aucun versement en attente.</p> : (
        <div className="space-y-2">
          {data.pending_deposits.map((d) => (
            <div key={d.id} data-testid={`pending-deposit-${d.id}`} className="flex flex-wrap items-center justify-between gap-2 border border-amber-200 bg-amber-50 rounded-xl px-4 py-3">
              <div className="text-sm">
                <span className="font-bold text-brand-ink">{fcfa(d.amount)}</span>
                <span className="text-brand-muted"> · {d.seller_name} · {d.operator === "flooz" ? "Moov" : "Togocel"}{d.reference ? ` · réf ${d.reference}` : ""}</span>
                {d.proof_path ? (
                  <a href={FILE_BASE + d.proof_path} target="_blank" rel="noreferrer" data-testid={`proof-${d.id}`} className="ml-2 inline-flex items-center gap-1 text-brand underline">
                    <ImageIcon className="w-3.5 h-3.5" /> Voir la preuve
                  </a>
                ) : (
                  <span className="ml-2 text-xs text-red-500">⚠ aucune preuve jointe</span>
                )}
              </div>
              <div className="flex gap-2">
                <button disabled={busy === d.id} onClick={() => act(d.id, "validate")} data-testid={`validate-deposit-${d.id}`}
                  className="bg-green-600 text-white hover:bg-green-700 rounded-full px-3 py-1.5 text-sm font-semibold flex items-center gap-1 disabled:opacity-60"><Check className="w-4 h-4" /> Valider</button>
                <button disabled={busy === d.id} onClick={() => act(d.id, "reject")} data-testid={`reject-deposit-${d.id}`}
                  className="bg-white border border-red-300 text-red-600 hover:bg-red-50 rounded-full px-3 py-1.5 text-sm font-semibold flex items-center gap-1 disabled:opacity-60"><X className="w-4 h-4" /> Rejeter</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
